import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Attachment, ChangeLogChangeType, ChangeLogEntityType } from "../API";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../contexts/UserContext";
import {
  capitalizeFirst,
  convertUtcToLocal,
  deleteFileFromS3,
  getFileTypeIcon,
  getFriendlyFileTypeName,
  getSortedAssociatedMessages,
  uploadFileToS3,
  logToConsole,
} from "../utilities/utilities";
import {
  createAttachmentWithPlatformGuards,
  deleteAttachmentWithPlatformGuards,
  updateAttachmentWithPlatformGuards,
} from "../graphql/mutations";
import "../styleSheets/attachmentViewEdit.css";
import "../styleSheets/attachmentEdit.css";
import "../styleSheets/commonElements.css";
import "../styleSheets/buttons.css";
import "../styleSheets/formFields.css";
import { useTranslation } from "react-i18next";
import { queryKeys } from "../graphql/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { TooltipButton } from "../utilities/ToolTipButton";
import FormFieldRow from "./FormFieldRow";
import ConfirmationModal from "./ui/ConfirmationModal";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import {
  FieldErrorMap,
  requiredFieldValidator,
  trimFormValues,
  validateFormFields,
} from "../utilities/formValidation";
import { useUnsavedChangesModalGuard } from "../utilities/formValidation";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import { useAttachments } from "../graphql/hooks";
import {
  getLocalizedStructuredPlatformLimitErrorMessage,
  getLocalizedPlatformLimitErrorMessage,
} from "../utilities/platformLimitErrors";
import {
  type ChangeLogEntryInput,
  createChangeRequestId,
  logChanges,
} from "../graphql/fetchers";

type AttachmentFormValues = {
  name: string;
  description: string;
};

const ATTACHMENT_VALIDATORS = {
  name: [requiredFieldValidator("required")],
  description: [requiredFieldValidator("required")],
};

type AttachmentAuditSnapshot = {
  nameComparable: string;
  descriptionComparable: string;
  fileIdentityComparable: string;
  fileNameComparable: string;
};

type AttachmentChangedFields = {
  name?: {
    old: string;
    new: string;
  };
  description?: {
    old: string;
    new: string;
  };
  file?: {
    old: string;
    new: string;
  };
};

type AttachmentUploadResult =
  | {
      status: "skipped";
    }
  | {
      status: "succeeded";
      s3Key: string;
    }
  | {
      status: "failed";
      error: unknown;
    };

const EMPTY_ATTACHMENT_AUDIT_SNAPSHOT: AttachmentAuditSnapshot = {
  nameComparable: "",
  descriptionComparable: "",
  fileIdentityComparable: "",
  fileNameComparable: "",
};

const ATTACHMENT_EDIT_CHANGE_LOG_METADATA = {
  source: "UI",
  page: "AttachmentEdit",
  device: "web",
} as const;

const ATTACHMENT_DELETE_CHANGE_LOG_METADATA = {
  source: "UI",
  action: "delete_confirmed",
} as const;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getAttachmentSnapshot(
  attachment: Pick<Attachment, "name" | "description" | "s3Key"> | null,
): AttachmentAuditSnapshot {
  if (!attachment) {
    return EMPTY_ATTACHMENT_AUDIT_SNAPSHOT;
  }

  return {
    nameComparable: (attachment.name ?? "").trim(),
    descriptionComparable: (attachment.description ?? "").trim(),
    fileIdentityComparable: (attachment.s3Key ?? "").trim(),
    fileNameComparable: (attachment.s3Key?.split("/").pop() ?? "").trim(),
  };
}

function getAttachmentDirtyCheckSnapshot(
  snapshot: AttachmentAuditSnapshot,
): string {
  return JSON.stringify({
    name: snapshot.nameComparable,
    description: snapshot.descriptionComparable,
    fileIdentity: snapshot.fileIdentityComparable,
    fileName: snapshot.fileNameComparable,
  });
}

function getOpenedAttachmentSnapshot(
  sourceAttachment: Attachment | null,
  fetchedAttachment: Attachment | null,
): AttachmentAuditSnapshot {
  return getAttachmentSnapshot(sourceAttachment ?? fetchedAttachment);
}

function getChangedFields(
  before: AttachmentAuditSnapshot,
  after: AttachmentAuditSnapshot,
): AttachmentChangedFields {
  const changedFields: AttachmentChangedFields = {};

  if (before.nameComparable !== after.nameComparable) {
    changedFields.name = {
      old: before.nameComparable,
      new: after.nameComparable,
    };
  }

  if (before.descriptionComparable !== after.descriptionComparable) {
    changedFields.description = {
      old: before.descriptionComparable,
      new: after.descriptionComparable,
    };
  }

  if (before.fileIdentityComparable !== after.fileIdentityComparable) {
    changedFields.file = {
      old: before.fileNameComparable,
      new: after.fileNameComparable,
    };
  }

  return changedFields;
}

function buildSavedAttachmentSnapshot(
  savedAttachment: Partial<Attachment> & { id: string },
  previousAttachment: Attachment | null,
  userId: string,
): Attachment {
  const timestamp = new Date().toISOString();

  return {
    __typename: "Attachment",
    id: savedAttachment.id,
    name: savedAttachment.name ?? null,
    description: savedAttachment.description ?? null,
    type: savedAttachment.type ?? previousAttachment?.type ?? null,
    status: savedAttachment.status ?? previousAttachment?.status ?? null,
    size: savedAttachment.size ?? previousAttachment?.size ?? null,
    s3Key: savedAttachment.s3Key ?? previousAttachment?.s3Key ?? null,
    uploadDate:
      savedAttachment.uploadDate ?? previousAttachment?.uploadDate ?? null,
    userprofileID: savedAttachment.userprofileID ?? userId,
    messages: previousAttachment?.messages ?? savedAttachment.messages ?? null,
    createdAt:
      savedAttachment.createdAt ?? previousAttachment?.createdAt ?? timestamp,
    updatedAt: savedAttachment.updatedAt ?? timestamp,
  };
}

function buildAttachmentStructuredError(
  code: string,
  details?: Record<string, unknown>,
): Error & { guardrailCode?: string } {
  const error = new Error(
    JSON.stringify(
      details && Object.keys(details).length > 0 ? { code, details } : { code },
    ),
  ) as Error & {
    guardrailCode?: string;
  };
  error.guardrailCode = code;
  return error;
}

function getNormalizedAttachmentExtension(fileName: string): string {
  const trimmedFileName = fileName.trim();
  const extensionIndex = trimmedFileName.lastIndexOf(".");

  if (
    !trimmedFileName ||
    extensionIndex < 0 ||
    extensionIndex === trimmedFileName.length - 1
  ) {
    return "";
  }

  return trimmedFileName.slice(extensionIndex + 1).trim().toLowerCase();
}

function getLocalAttachmentCategory(fileType: string): "general" | "video" | null {
  const normalized = fileType.trim().toLowerCase();

  if (!normalized || normalized === "application/octet-stream") {
    return null;
  }

  if (normalized.startsWith("video/")) {
    return "video";
  }

  if (
    normalized.startsWith("application/") ||
    normalized.startsWith("audio/") ||
    normalized.startsWith("image/") ||
    normalized.startsWith("text/")
  ) {
    return "general";
  }

  return null;
}

function runLocalAttachmentPrecheck(file: File): Error | null {
  if (!getNormalizedAttachmentExtension(file.name)) {
    return buildAttachmentStructuredError("invalid_extension");
  }

  if (file.type.trim() && getLocalAttachmentCategory(file.type) === null) {
    return buildAttachmentStructuredError("invalid_file_category");
  }

  return null;
}

function AttachmentEdit() {
  const location = useLocation();
  const navigate = useNavigate();
  const { attachment: routeAttachmentId } = useParams<{
    attachment?: string;
  }>();
  const { setGuard } = useNavigationGuard();
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();
  const client = generateClient();
  const { user, isAuthenticated } = useUser();
  const userId = user?.userId ?? "";
  const attachmentsQ = useAttachments(userId, isAuthenticated && !!userId);
  const stateAttachment = (location.state as Attachment) || null;
  const attachmentId = (routeAttachmentId ?? stateAttachment?.id ?? "").trim();
  const sourceAttachment =
    !attachmentId || stateAttachment?.id === attachmentId
      ? stateAttachment
      : null;
  const isNew = !attachmentId;
  const fetchedAttachment = useMemo(
    () =>
      (attachmentsQ.data ?? []).find((item) => item.id === attachmentId) ??
      null,
    [attachmentId, attachmentsQ.data],
  );
  const [currentAttachment, setCurrentAttachment] = useState<Attachment | null>(
    sourceAttachment,
  );
  const attachment = currentAttachment ?? sourceAttachment;
  const auditBaselineSnapshotRef = useRef<AttachmentAuditSnapshot>(
    isNew
      ? EMPTY_ATTACHMENT_AUDIT_SNAPSHOT
      : getAttachmentSnapshot(sourceAttachment),
  );
  const initialUploadTimestamp = useMemo(() => new Date().toISOString(), []);
  const attachmentDisplayFileName = useMemo(
    () => attachment?.s3Key?.split("/").pop() ?? "",
    [attachment?.s3Key],
  );

  const [formValues, setFormValues] = useState<AttachmentFormValues>({
    name: attachment?.name ?? "",
    description: attachment?.description ?? "",
  });
  const [fieldErrors, setFieldErrors] = useState<
    FieldErrorMap<AttachmentFormValues> & { file?: string }
  >({});
  const [formError, setFormError] = useState("");

  const [type, setType] = useState(attachment?.type ?? "");
  const [status, setStatus] = useState(attachment?.status ?? "");
  const [, setSize] = useState(attachment?.size ?? 0);
  const [s3Key, setS3Key] = useState(attachment?.s3Key ?? "");
  const [uploadDate, setUploadDate] = useState(
    attachment?.uploadDate ?? initialUploadTimestamp,
  );

  const associatedMessages = useMemo(
    () => getSortedAssociatedMessages(attachment),
    [attachment],
  );

  const [displayType, setDisplayType] = useState(attachment?.type ?? "");
  const [displaySize, setDisplaySize] = useState(attachment?.size ?? "");
  const [displayFileName, setDisplayFileName] = useState(
    attachmentDisplayFileName,
  );
  const [initialSnapshot, setInitialSnapshot] = useState<string>(
    isNew
      ? getAttachmentDirtyCheckSnapshot(EMPTY_ATTACHMENT_AUDIT_SNAPSHOT)
      : getAttachmentDirtyCheckSnapshot(
          getOpenedAttachmentSnapshot(sourceAttachment, fetchedAttachment),
        ),
  );
  const Icon = useMemo(
    () => getFileTypeIcon(displayType || type),
    [displayType, type],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const uploadModalRef = useRef<HTMLDivElement | null>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false);
  const [saveResultTitle, setSaveResultTitle] = useState("");
  const [saveResultMessage, setSaveResultMessage] = useState("");
  const [saveShouldReturnToPanel, setSaveShouldReturnToPanel] = useState(false);
  const isLoadingExistingAttachment =
    !isNew && !attachment && attachmentsQ.isLoading;
  const controlsDisabled = isLoadingExistingAttachment || isSaving;

  const normalizedValues = useMemo(
    () => trimFormValues(formValues),
    [formValues],
  );
  const currentDirtySnapshot = useMemo(
    () =>
      getAttachmentDirtyCheckSnapshot({
        nameComparable: normalizedValues.name,
        descriptionComparable: normalizedValues.description,
        fileIdentityComparable: (file ? selectedFileName : s3Key).trim(),
        fileNameComparable: (file ? selectedFileName : displayFileName).trim(),
      }),
    [displayFileName, file, normalizedValues, s3Key, selectedFileName],
  );
  const hasUnsavedChanges = useMemo(() => {
    return currentDirtySnapshot !== initialSnapshot;
  }, [currentDirtySnapshot, initialSnapshot]);
  const {
    isPromptOpen,
    requestNavigation,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesModalGuard(hasUnsavedChanges);
  useEffect(() => {
    if (isNew) {
      setCurrentAttachment(null);
      return;
    }

    setCurrentAttachment(sourceAttachment);
  }, [attachmentId, isNew, sourceAttachment]);

  useEffect(() => {
    if (!isNew && !currentAttachment && fetchedAttachment) {
      setCurrentAttachment(fetchedAttachment);
    }
  }, [currentAttachment, fetchedAttachment, isNew]);

  useEffect(() => {
    const openedSnapshot = isNew
      ? EMPTY_ATTACHMENT_AUDIT_SNAPSHOT
      : getOpenedAttachmentSnapshot(sourceAttachment, fetchedAttachment);

    auditBaselineSnapshotRef.current = openedSnapshot;
    setInitialSnapshot(getAttachmentDirtyCheckSnapshot(openedSnapshot));
  }, [fetchedAttachment, isNew, sourceAttachment]);

  useEffect(() => {
    const nextFormValues = {
      name: attachment?.name ?? "",
      description: attachment?.description ?? "",
    };
    const nextType = attachment?.type ?? "";
    const nextStatus = attachment?.status ?? "";
    const nextSize = attachment?.size ?? 0;
    const nextS3Key = attachment?.s3Key ?? "";
    const nextUploadDate = attachment?.uploadDate ?? initialUploadTimestamp;
    const nextDisplayFileName = attachmentDisplayFileName;

    setFormValues(nextFormValues);
    setFieldErrors({});
    setFormError("");
    setType(nextType);
    setStatus(nextStatus);
    setSize(nextSize);
    setS3Key(nextS3Key);
    setUploadDate(nextUploadDate);
    setDisplayType(nextType);
    setDisplaySize(nextSize);
    setDisplayFileName(nextDisplayFileName);
    setFile(null);
    setSelectedFileName("");
  }, [
    attachment?.description,
    attachment?.id,
    attachment?.name,
    attachment?.s3Key,
    attachment?.size,
    attachment?.status,
    attachment?.type,
    attachment?.uploadDate,
    attachmentDisplayFileName,
    initialUploadTimestamp,
  ]);

  useEffect(() => {
    setGuard(hasUnsavedChanges ? requestNavigation : null);
    return () => setGuard(null);
  }, [hasUnsavedChanges, requestNavigation, setGuard]);

  useEffect(() => {
    if (!isModalOpen) return;

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const frameId = window.requestAnimationFrame(() => {
      const focusableElements = Array.from(
        uploadModalRef.current?.querySelectorAll<HTMLElement>(
          FOCUSABLE_SELECTOR,
        ) ?? [],
      ).filter((element) => element.tabIndex !== -1);

      (focusableElements[0] ?? uploadModalRef.current)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);

      const previous = previousActiveElementRef.current;
      previousActiveElementRef.current = null;

      if (previous && document.contains(previous)) {
        previous.focus();
      }
    };
  }, [isModalOpen]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = Array.from(
      uploadModalRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ??
        [],
    ).filter((element) => element.tabIndex !== -1);

    if (focusableElements.length === 0) {
      event.preventDefault();
      uploadModalRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey) {
      if (activeElement === firstElement || activeElement === uploadModalRef.current) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (activeElement === lastElement || activeElement === uploadModalRef.current) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = e.target.files?.[0] || null;

    if (!nextFile) {
      setSelectedFileName("");
      setFile(null);
      return;
    }

    setSelectedFileName(nextFile.name);
    setFile(nextFile);
  };

  const setFieldValue = (field: keyof AttachmentFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setFormError("");
  };

  const validateAttachmentForm = () => {
    const errors = validateFormFields(
      normalizedValues,
      ATTACHMENT_VALIDATORS,
    ) as FieldErrorMap<AttachmentFormValues> & {
      file?: string;
    };

    if (errors.name) errors.name = t("file.validation.nameRequired");
    if (errors.description)
      errors.description = t("file.validation.descriptionRequired");
    if (displayFileName.trim() === "") {
      errors.file = t("file.validation.fileRequired");
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError(t("file.validation.formErrors"));
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!file) {
      setFieldErrors((prev) => ({
        ...prev,
        file: t("file.validation.fileRequired"),
      }));
      return;
    }
    setDisplayFileName(file.name);
    setDisplaySize(parseFloat((file.size / (1024 * 1024)).toFixed(2)));
    setDisplayType(getFriendlyFileTypeName(file.type));
    setUploadDate("");
    setStatus(t("file.attached"));
    setFieldErrors((prev) => ({ ...prev, file: undefined }));
    closeModal();
  };

  const handleDeleteAttachment = () => {
    if (associatedMessages.length > 0 || isNew || !attachment?.id) return;
    setFormError("");
    setIsDeleteModalOpen(true);
  };

  const handleKeepAttachment = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDiscardChanges = () => {
    if (hasUnsavedChanges && !isNew && attachment?.id) {
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.ATTACHMENT,
          entityId: attachment.id,
          changeType: ChangeLogChangeType.ABANDON,
          metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
          userId,
        },
      ]);
    }

    confirmNavigation();
  };

  const handleConfirmDeleteAttachment = async () => {
    if (isDeleting || associatedMessages.length > 0 || isNew || !attachment?.id)
      return;

    setIsDeleting(true);
    setFormError("");

    try {
      await client.graphql({
        query: deleteAttachmentWithPlatformGuards,
        variables: { id: attachment.id },
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeys.attachments(userId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.effectivePlatformLimits(userId),
      });
      const deleteRequestId = createChangeRequestId();
      const deletedFileName = (attachment.s3Key?.split("/").pop() ?? "").trim();
      logToConsole("log", "Deleted file name for change log:", deletedFileName);
      void logChanges([
        {
          requestId: deleteRequestId,
          entityType: ChangeLogEntityType.ATTACHMENT,
          entityId: attachment.id,
          changeType: ChangeLogChangeType.DELETE,
          fieldName: "attachmentRecord",
          oldValue: deletedFileName,
          newValue: null,
          metadata: ATTACHMENT_DELETE_CHANGE_LOG_METADATA,
          userId,
        },
      ]);
      setIsDeleteModalOpen(false);
      navigate("/Panel?selectedTab=files");
    } catch (error) {
      logToConsole("error", "Error deleting attachment:", error);
      setFormError(
        getLocalizedPlatformLimitErrorMessage(
          t,
          error,
          t("file.errorMessage.fileDeleteFailed"),
        ),
      );
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveFileToAttachment = async (): Promise<AttachmentUploadResult> => {
    try {
      if (!file) {
        return {
          status: "skipped",
        };
      }

      const precheckError = runLocalAttachmentPrecheck(file);
      if (precheckError) {
        logToConsole(
          "error",
          "Attachment local precheck failed before upload:",
          precheckError,
        );
        return {
          status: "failed",
          error: precheckError,
        };
      }

      const authSession = await fetchAuthSession();
      const identityId = authSession.identityId?.toString().trim() ?? "";
      if (!identityId) {
        logToConsole(
          "error",
          "Attachment upload aborted because identityId was unavailable.",
          {
            fileName: file.name,
          },
        );
        return {
          status: "failed",
          error: buildAttachmentStructuredError("file_upload_failed"),
        };
      }

      const generatedS3Key = await uploadFileToS3(file, identityId);
      if (!generatedS3Key) {
        return {
          status: "failed",
          error: new Error("File upload did not return an S3 key."),
        };
      }

      return {
        status: "succeeded",
        s3Key: generatedS3Key,
      };
    } catch (error) {
      logToConsole("error", "Error uploading the file:", error);
      return {
        status: "failed",
        error,
      };
    }
  };

  const cleanupUploadedAttachmentFile = async (
    uploadedS3Key: string,
    contextLabel: string,
  ) => {
    const cleanupResult = await deleteFileFromS3(uploadedS3Key);

    if (cleanupResult === "failed") {
      logToConsole(
        "error",
        `Best-effort attachment cleanup failed after ${contextLabel}:`,
        {
          s3Key: uploadedS3Key,
          cleanupResult,
        },
      );
    }
  };

  const getAttachmentCreateSaveErrorMessage = (error: unknown): string =>
    getLocalizedStructuredPlatformLimitErrorMessage(t, error) ??
    t("file.errorMessage.fileSaveFailed");

  const handleSaveAttachment = async () => {
    if (!isNew && !attachment?.id) {
      setFormError(t("errorMessage.failedLoad"));
      return;
    }

    if (!hasUnsavedChanges) {
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("errorMessage.noChangesTitle"));
      setSaveResultMessage(t("errorMessage.noChangesToSave"));
      setIsSaveResultModalOpen(true);
      return;
    }
    if (!validateAttachmentForm()) return;

    let dateUploaded: string = uploadDate
      ? new Date(uploadDate).toISOString()
      : new Date().toISOString();
    let newS3Key: string | undefined = s3Key;
    let uploadedS3KeyForCleanup: string | null = null;
    const beforeAuditSnapshot = auditBaselineSnapshotRef.current;

    setIsSaving(true);
    try {
      if (!isNew) {
        if (!attachment?.id) {
          setFormError(t("file.errorMessage.fileUpdateFailed"));
          return;
        }

        if (file?.name) {
          const uploadResult = await saveFileToAttachment();
          if (uploadResult.status === "failed") {
            setFormError(
              getLocalizedPlatformLimitErrorMessage(
                t,
                uploadResult.error,
                t("file.errorMessage.fileUploadFailed"),
              ),
            );
            return;
          }

          if (uploadResult.status === "succeeded") {
            newS3Key = uploadResult.s3Key;
            uploadedS3KeyForCleanup = uploadResult.s3Key;
            dateUploaded = new Date().toISOString();
          }
        }
        if (newS3Key != null) {
          const input = {
            id: attachment.id,
            name: normalizedValues.name,
            description: normalizedValues.description,
            ...(file?.name ? { s3Key: newS3Key } : {}),
          };
          const result: any = await client.graphql({
            query: updateAttachmentWithPlatformGuards,
            variables: { input },
          });
          const updatedAttachment =
            result?.data?.updateAttachmentWithPlatformGuards;
          uploadedS3KeyForCleanup = null;
          logToConsole("log", "data sent:", input);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: queryKeys.attachments(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.effectivePlatformLimits(userId),
            }),
          ]);
          const savedAttachment = buildSavedAttachmentSnapshot(
            updatedAttachment ?? input,
            attachment,
            user.userId,
          );
          setS3Key(newS3Key);
          setUploadDate(dateUploaded);
          const afterAuditSnapshot = getAttachmentSnapshot(savedAttachment);
          setCurrentAttachment(savedAttachment);
          auditBaselineSnapshotRef.current = afterAuditSnapshot;
          setInitialSnapshot(
            getAttachmentDirtyCheckSnapshot(afterAuditSnapshot),
          );
          const updateRequestId = createChangeRequestId();
          const updateAuditEntries: ChangeLogEntryInput[] = [
            {
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.ATTACHMENT,
              entityId: attachment.id,
              changeType: ChangeLogChangeType.UPDATE,
              metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            },
          ];
          const changedFields = getChangedFields(
            beforeAuditSnapshot,
            afterAuditSnapshot,
          );

          if (changedFields.name) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.ATTACHMENT,
              entityId: attachment.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "name",
              oldValue: changedFields.name.old,
              newValue: changedFields.name.new,
              metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          if (changedFields.description) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.ATTACHMENT,
              entityId: attachment.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "description",
              oldValue: changedFields.description.old,
              newValue: changedFields.description.new,
              metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          if (changedFields.file) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.ATTACHMENT,
              entityId: attachment.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "file",
              oldValue: changedFields.file.old,
              newValue: changedFields.file.new,
              metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          void logChanges(updateAuditEntries);
          if (routeAttachmentId !== attachment.id) {
            navigate(`/AttachmentEdit/${attachment.id}`, {
              replace: true,
              state: savedAttachment,
            });
          }
          setSelectedFileName("");
          setFile(null);
        }
      } else {
        const uploadResult = await saveFileToAttachment();
        if (uploadResult.status !== "succeeded") {
          const uploadFailureMessage = getAttachmentCreateSaveErrorMessage(
            uploadResult.status === "failed" ? uploadResult.error : null,
          );
          setFormError(uploadFailureMessage);
          setSaveShouldReturnToPanel(false);
          setSaveResultTitle(t("common.saveWindowTitle"));
          setSaveResultMessage(uploadFailureMessage);
          setIsSaveResultModalOpen(true);
          return;
        }

        newS3Key = uploadResult.s3Key;
        uploadedS3KeyForCleanup = uploadResult.s3Key;
        dateUploaded = new Date().toISOString();
        const input = {
          name: normalizedValues.name,
          description: normalizedValues.description,
          s3Key: newS3Key,
        };
        const result: any = await client.graphql({
          query: createAttachmentWithPlatformGuards,
          variables: { input },
        });
        const createdAttachment =
          result?.data?.createAttachmentWithPlatformGuards;        
        logToConsole("log", "New Attachment data sent:", input);
        if (!createdAttachment?.id) {
          throw new Error("Attachment create did not return an id.");
        }
        uploadedS3KeyForCleanup = null;
        setS3Key(newS3Key);
        setDisplayFileName(newS3Key.split("/").pop() ?? "");
        setUploadDate(dateUploaded);
        const savedAttachment = buildSavedAttachmentSnapshot(
          createdAttachment,
          null,
          user.userId,
        );
        const savedAttachmentSnapshot = getAttachmentSnapshot(savedAttachment);
        auditBaselineSnapshotRef.current = savedAttachmentSnapshot;
        setInitialSnapshot(
          getAttachmentDirtyCheckSnapshot(savedAttachmentSnapshot),
        );
        setCurrentAttachment(savedAttachment);
        const createRequestId = createChangeRequestId();
        const createdFileName = (
          createdAttachment.s3Key?.split("/").pop() ?? ""
        ).trim();
        void logChanges([
          {
            requestId: createRequestId,
            entityType: ChangeLogEntityType.ATTACHMENT,
            entityId: createdAttachment.id,
            changeType: ChangeLogChangeType.CREATE,
            metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
            userId,
          },
          {
            requestId: createRequestId,
            entityType: ChangeLogEntityType.ATTACHMENT,
            entityId: createdAttachment.id,
            changeType: ChangeLogChangeType.CREATE,
            fieldName: "fileName",
            oldValue: "",
            newValue: createdFileName,
            metadata: ATTACHMENT_EDIT_CHANGE_LOG_METADATA,
            userId,
          },
        ]);
        navigate(`/AttachmentEdit/${savedAttachment.id}`, {
          replace: true,
          state: savedAttachment,
        });
        setSelectedFileName("");
        setFile(null);
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.attachments(userId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.effectivePlatformLimits(userId),
        }),
      ]);
      setFieldErrors({});
      setFormError("");
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("common.saveWindowTitle"));
      setSaveResultMessage(t("file.onScreenMessage.fileSaveSuccess"));
      setIsSaveResultModalOpen(true);
    } catch (error) {
      if (uploadedS3KeyForCleanup) {
        await cleanupUploadedAttachmentFile(
          uploadedS3KeyForCleanup,
          isNew
            ? "createAttachmentWithPlatformGuards"
            : "updateAttachmentWithPlatformGuards",
        );
      }

      if (!isNew) {
        setStatus("Error");
        logToConsole("error", "Error updating attachment:", error);
        setFormError(
          getLocalizedPlatformLimitErrorMessage(
            t,
            error,
            t("file.errorMessage.fileUpdateFailed"),
          ),
        );
      } else {
        logToConsole("error", "Error creating new attachment:", error);
        const createFailureMessage = getAttachmentCreateSaveErrorMessage(error);
        setFormError(createFailureMessage);
        setSaveShouldReturnToPanel(false);
        setSaveResultTitle(t("common.saveWindowTitle"));
        setSaveResultMessage(createFailureMessage);
        setIsSaveResultModalOpen(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    requestNavigation(() => navigate("/Panel?selectedTab=files"));
  };
  const handleContinueEditingAfterSave = () => {
    setIsSaveResultModalOpen(false);
  };

  const handleCloseAfterSave = () => {
    setIsSaveResultModalOpen(false);
    if (saveShouldReturnToPanel) {
      navigate("/Panel?selectedTab=files");
    }
  };
  const canDeleteAttachment =
    !isNew && !!attachment?.id && associatedMessages.length === 0;

  if (!isNew && !attachment && !attachmentsQ.isLoading) {
    return <Navigate to="/Panel?selectedTab=files" replace />;
  }

  if (isSaving) {
    return <BlockingPageLoader message={t("common.saving")} />;
  }

  return (
    <div className="attachment-edit-page">
      <div className="attachment-topbar">
        <div className="attachment-topbar__nav">
          <div className="attachment-topbar__container">
            <div className="attachment-topbar__row">
              <button
                id="cancel-btn"
                type="button"
                className="btn btn--gray btn--size-md btn--icon-left"
                onClick={handleCancel}
              >
                <div
                  className="btn__icon icon-chevron-left"
                  aria-hidden="true"
                />
                {t("common.cancel")}
              </button>

              <div className="attachment-topbar__title">
                {capitalizeFirst(t("file.file"))}
              </div>

              <div className="attachment-topbar__actions">
                <button
                  id="save-btn"
                  type="button"
                  className="btn btn--green btn--size-md btn--icon-left"
                  onClick={handleSaveAttachment}
                  disabled={controlsDisabled}
                >
                  <span className="btn__icon icon-check" aria-hidden="true" />
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className="attachment-topbar__titleBelow">
              {capitalizeFirst(t("file.file"))}
            </div>
          </div>
        </div>
      </div>

      <div className="attachment-edit-section">
        {isLoadingExistingAttachment ? (
          <div className="attachment-formError">{t("common.loading")}</div>
        ) : null}
        {formError ? (
          <div className="attachment-formError">{formError}</div>
        ) : null}
        <div className="attachment-edit-form">
          <div className="attachment-selector-container">
            <div
              className={`attachment-info-container${fieldErrors.file ? " attachment-info-container--error" : ""}`}
            >
              <Icon className="attachment-icon" aria-hidden="true" />
              <div className="attachment-metadata-container">
                <div className="attachment-name">
                  {displayFileName !== ""
                    ? displayFileName
                    : t("file.noFileSelected")}
                </div>
                {displayType !== "" && (
                  <div className="attachment-metadata-element-container">
                    <div className="attachment-metadata-element">
                      {t("common.status")}: {status}
                    </div>
                    <div className="attachment-metadata-element">
                      | {t("common.type")}: {displayType}
                    </div>
                    <div className="attachment-metadata-element">
                      {" "}
                      | {t("common.size")}: {displaySize} Mb
                    </div>
                    <div className="attachment-metadata-element">
                      {uploadDate && (
                        <>
                          {" | " + t("common.uploadedOn")}:{" "}
                          {convertUtcToLocal(uploadDate, "NoDay")}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

          {isModalOpen && (
              <div className="backdrop" onClick={closeModal}>
                <div
                  className="modal"
                  ref={uploadModalRef}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="attachment-upload-dialog-title"
                  tabIndex={-1}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleModalKeyDown}
                >
                  <button
                    type="button"
                    className="close"
                    onClick={closeModal}
                    aria-label={t("common.close")}
                  >
                    &times;
                  </button>
                  <h2 id="attachment-upload-dialog-title">
                    {t("file.onScreenMessage.selectFileUpload")}
                  </h2>
                  <input
                    id="file-upload-input"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <button
                    id="attach-file-btn"
                    onClick={handleUpload}
                    className="attachment-upload__action"
                  >
                    {t("file.attach")}
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={openModal}
              className="btn btn--white btn--size-md"
              id="select-file-btn"
              disabled={controlsDisabled}
              aria-describedby={fieldErrors.file ? "attachment-file-error" : undefined}
            >
              <span className="btn__icon icon-upload" aria-hidden="true" />
              {isNew ? t("file.selectFile") : t("file.replaceFile")}
            </button>
          </div>

          {fieldErrors.file ? (
            <p id="attachment-file-error" className="attachment-fieldError" aria-live="polite">
              {fieldErrors.file}
            </p>
          ) : null}

          <FormFieldRow
            id="attachment-name"
            label={capitalizeFirst(t("file.alias"))}
            required
            error={fieldErrors.name}
            isWritten={normalizedValues.name.length > 0 && !fieldErrors.name}
          >
            <input
              id="attachment-name"
              className="attachment-field-value"
              type="text"
              value={formValues.name}
              disabled={controlsDisabled}
              onChange={(e) => setFieldValue("name", e.target.value)}
            />
          </FormFieldRow>

          <FormFieldRow
            id="attachment-description"
            label={t("file.tableColumn.description")}
            required
            error={fieldErrors.description}
            isWritten={
              normalizedValues.description.length > 0 &&
              !fieldErrors.description
            }
          >
            <textarea
              id="attachment-description"
              className="attachment-description-content-value attachment-description-content-value--field"
              value={formValues.description}
              disabled={controlsDisabled}
              onChange={(e) => setFieldValue("description", e.target.value)}
              rows={8}
            />
          </FormFieldRow>
        </div>

        <div className="attachment-delete-wrapper">
          <TooltipButton
            label={t("file.deleteFile")}
            id="delete-attachment-btn"
            tooltip={t("file.onScreenMessage.deleteFileBlocked")}
            disabled={!canDeleteAttachment || isSaving}
            onClick={handleDeleteAttachment}
            buttonClassName="btn btn--transparent-no-move delete-attachment-button"
            buttonIcon={true}
            buttonIconClassName="icon-trash"
          />
        </div>
      </div>

      <div className="attachment-edit-section">
        <div className="attachment-edit-form">
          <div className="attachment-details-title">
            {t("common.associatedMessages")}
          </div>

          <div className="associated-messages">
            {associatedMessages.length === 0 ? (
              <div className="associated-message associated-message--empty">
                {t("common.noAssociatedMessages")}
              </div>
            ) : (
              associatedMessages.map((m: any, index: number) => {
                const message = m?.message ?? m;
                const messageId =
                  message?.id ?? m?.id ?? `associated-message-${index}`;
                return (
                  <div key={messageId} className="associated-message">
                    {message?.title ?? t("message.untitledMessage")}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isPromptOpen}
        title={t("errorMessage.unsavedChanges")}
        message={t("errorMessage.unsavedChangesPrompt")}
        primaryLabel={t("common.discardChanges")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleConfirmDiscardChanges}
        onSecondary={cancelNavigation}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title={t("file.deleteFile")}
        message={t("file.onScreenMessage.deleteFileConfirm")}
        primaryLabel={isDeleting ? t("common.deleting") : t("common.delete")}
        secondaryLabel={t("common.keep")}
        onPrimary={handleConfirmDeleteAttachment}
        onSecondary={handleKeepAttachment}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
        primaryDisabled={isDeleting}
        secondaryDisabled={isDeleting}
      />
      <ConfirmationModal
        isOpen={isSaveResultModalOpen}
        title={saveResultTitle}
        message={saveResultMessage}
        primaryLabel={
          saveShouldReturnToPanel ? t("common.finishEdit") : t("common.close")
        }
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleCloseAfterSave}
        onSecondary={handleContinueEditingAfterSave}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
    </div>
  );
}

export default AttachmentEdit;
